<template>
  <div class="m-3" style="height: 90vh">
    <div class="grid grid-cols-12 items-center content-center gap-2">
      <el-select
          class="input-label col-span-12 md:col-span-2"
          v-model="selectedValue"
          @change="applyToSelected"
      >
        <template v-if="$can('read', '/hr/commissions')">
          <el-option
              key="export"
              :label="$t('views.common.listView.selectedRows.export')"
              value="export"
          >
          </el-option>
        </template>
        <template v-if="$can('delete', '/hr/commissions')">
          <el-option
            key="delete"
            :label="$t('views.common.listView.selectedRows.delete')"
            value="delete"
          >
          </el-option>
        </template>
      </el-select>

      <el-input
          :placeholder="$t('views.common.listView.searchRows')"
          class="w-full col-span-12 md:col-span-7"
          :prefix-icon="Search"
          v-model="searchTerm"
      />

      <div class="w-full col-span-12 md:col-span-3">
        <template v-if="$can('create', '/hr/commissions')">
          <horizontal-button
              :title="$t('views.hr.commissions.newCommission')"
              @click.prevent="$router.push($Route.HR_COMMISSIONS_NEW_COMMISSION)"
          />
        </template>
      </div>
    </div>

    <ag-grid-vue
        class="ag-theme-alpine mt-4 md:mt-0"
        :columnDefs="columnDefs"
        :rowData="rowData"
        rowSelection="multiple"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 85vh"
        :pagination="true"
        :paginationAutoPageSize="true"
        :defaultColDef="defaultColDef"
        @cell-double-clicked="onCellDoubleClicked"
        :onGridReady="onGridReady"
    >
    </ag-grid-vue>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
import { AgGridVue } from 'ag-grid-vue3'
import { ColDef, GridApi, GridOptions } from 'ag-grid-community'
import HorizontalButton from '@/components/HorizontalButton.vue'

import Commission, { commissionI18nMessages } from '@/models/hr/Commission'
import { useI18n } from 'vue-i18n'
import { Search } from '@element-plus/icons-vue'
import {formatPostgresDate} from "@/utils/date";

export default defineComponent({
  name: 'commissions',

  setup() {
    const { t } = useI18n({
      messages: commissionI18nMessages
    })

    return {
      t,
      Search
    }
  },

  data() {
    const columnDefs: ColDef[] = []
    const rowData: Commission[] = []
    const gridOptions: GridOptions = {}
    const defaultColDef: ColDef = {}

    return {
      columnDefs,
      rowData,
      gridOptions,
      defaultColDef,

      searchTerm: '',
      selectedValue: '',
      gridApi: undefined as GridApi | undefined
    }
  },

  components: {
    AgGridVue,
    HorizontalButton
  },

  beforeMount() {
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
    }

    this.columnDefs = [
      {
        headerName: this.t('name'),
        field: 'name',
        width: 300,
        checkboxSelection: true,
        headerCheckboxSelection: true
      },
      {
        headerName: this.t('period'),
        field: 'period',
        width: 150,
        valueGetter: (params) => {
          const commission = params.node?.data as Commission
          if (!commission) return ''
          return this.t(commission.period);
        }
      },
      {
        headerName: this.t('models.common.date'),
        field: 'createdAt',
        width: 100,
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const commission = params.node?.data as Commission
          if (!commission) return ''
          if (commission.createdAt) {
            const createdAt = new Date(commission.createdAt)
            return formatPostgresDate(String(createdAt), 'DD/MM/YYYY')
          }
          return ''
        }
      },
      {
        headerName: this.t('models.common.time'),
        field: 'createdAt',
        width: 100,
        cellStyle: { direction: 'ltr' },
        valueGetter: (params) => {
          const commission = params.node?.data as Commission
          if (!commission) return ''
          if (commission.createdAt) {
            const createdAt = new Date(commission.createdAt)
            return formatPostgresDate(String(createdAt), 'hh:mm A')
          }
          return ''
        }
      },
      {
        headerName: this.t('user'),
        field: 'user.fullName',
        cellStyle: { textAlign: 'center' },
        valueGetter: (params) => {
          const commission = params.node?.data as Commission
          if (!commission) return ''
          if (commission.user) {
            return `${commission.user.name}`
          }
          return ''
        }
      },
    ]
  },

  mounted() {
  },

  watch: {
    searchTerm() {
      this.filterItems()
    }
  },

  methods: {
    async updateRowData() {
      this.gridApi?.showLoadingOverlay()

      try {
        const data = await this.$http.get<Commission[]>(Commission.ENDPOINT)
        this.rowData = data.map((commission) => Commission.from(commission))
      } catch (error) {
        this.$alertModal.showDanger({
          title: error.title,
          body: error.body
        })
      }

      this.gridApi?.hideOverlay()
    },

    async onGridReady(params: any) {
      this.gridApi = params.api
      await this.updateRowData()
    },

    async delete(commissions: Commission[]) {
      if (confirm(this.$t('views.common.listView.selectedRows.deleteConfirm'))) {
        try {
          await this.$http.delete(Commission.ENDPOINT, { data: commissions })
          await this.updateRowData()
          this.$alertModal.showSuccess({
            title: this.$t('views.common.listView.selectedRows.deleteSuccess')
          })
        } catch (error) {
          this.$alertModal.showDanger({
            title: error.title,
            body: error.body
          })
        }
      }
    },

    async applyToSelected() {
      if (this.selectedValue === 'delete' || this.selectedValue === 'export') {
        const selected = this.gridApi?.getSelectedRows()

        if (selected?.length) {
          if (this.selectedValue === 'delete') {
            // send request to archive
            await this.delete(selected)
          } else {
            this.gridApi?.exportDataAsCsv({ onlySelected: true })
            this.$alertModal.showSuccess({
              title: this.$t('views.common.listView.selectedRows.exportSuccess')
            })
          }

          // deselect
          selected.length = 0
        }
      }

      this.selectedValue = ''
    },

    filterItems() {
      this.gridApi?.setQuickFilter(this.searchTerm)
    },

    onCellDoubleClicked(event: any) {
      this.$router.push(this.$Route.HR_COMMISSIONS_COMMISSION.replace(':id', event.data.id))
    }
  }
})
</script>

<style></style>
